import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import {
  Accordion,
  Banner,
  Button,
  ButtonText,
  FormFeedback,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import {
  faListCheck,
  faTriangleExclamation
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CheckTypeMap,
  ECheckLifecycleStatus,
  ICheckResult
} from '@publishing-engine/domain/models';
import CheckList from '../IntegrityTab/CheckList';
import IntegrityStatusIcon from '../IntegrityTab/IntegrityStatusIcon';
import { IAuthorIntegrityChecks } from './useAuthorIntegrityChecks';
import { IIntegrityTally, runErrorMessage } from './authorIntegrityRules';
import RunningChecksModal from './RunningChecksModal';

type IProps = {
  integrity: IAuthorIntegrityChecks;
  submissionId: string;
  submissionContextId: string;
  /** Set when a submit attempt was refused because no run had happened yet. */
  showNotRunError?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
};

const statusLabels: Record<ECheckLifecycleStatus, string> = {
  [ECheckLifecycleStatus.NOT_STARTED]: 'Queued',
  [ECheckLifecycleStatus.PROCESSING]: 'In progress',
  [ECheckLifecycleStatus.COMPLETE]: 'Done',
  [ECheckLifecycleStatus.FAILED]: 'Failed'
};

const headerCopy =
  'You can run integrity checks on your submission to ensure that all requirements are met before proceeding.';

const IconBadge = () => (
  <Flex
    width={10}
    height={10}
    flexShrink={0}
    alignItems="center"
    justifyContent="center"
    borderRadius="small"
    backgroundColor="neutralSecondaryFill"
    color="actionSecondaryContent"
  >
    <FontAwesomeIcon icon={faListCheck} />
  </Flex>
);

const CardHeader = ({ action }: { action?: React.ReactNode }) => (
  <Flex
    justifyContent="space-between"
    alignItems="center"
    gap={4}
    paddingBottom={3}
    borderBottom="1px"
    borderBottomColor="neutralPrimaryBorder"
  >
    <Flex alignItems="center" gap={3}>
      <IconBadge />
      <Text textStyle="headingItem">Integrity Checks</Text>
    </Flex>
    {action}
  </Flex>
);

/**
 * Width comes from the column, not the tile, so the three of them stay equal
 * however long the labels grow.
 */
const Tile = ({
  label,
  count,
  borderColor,
  backgroundColor,
  color
}: {
  label: string;
  count: number;
  borderColor: string;
  backgroundColor: string;
  color: string;
}) => (
  <Flex
    flexDirection="column"
    justifyContent="center"
    paddingX={4}
    paddingY={3}
    borderWidth="1px"
    borderRadius="small"
    borderColor={borderColor}
    backgroundColor={backgroundColor}
    color={color}
  >
    <Text textStyle="headingSection" lineHeight={1}>
      {String(count).padStart(2, '0')}
    </Text>
    <Text textStyle="textContext">{label}</Text>
  </Flex>
);

/** The three tallies that summarise the run. */
const Tiles = ({ tally }: { tally: IIntegrityTally }) => (
  <Grid templateColumns="repeat(3, 1fr)" gap={3} marginTop={4} width="100%">
    <Tile
      label="Risk"
      count={tally.risk}
      borderColor="gray.300"
      backgroundColor="white"
      color="#B42318"
    />
    <Tile
      label="Warning"
      count={tally.warning}
      borderColor="gray.300"
      backgroundColor="white"
      color="#DC6803"
    />
    <Tile
      label="Passed"
      count={tally.passed}
      borderColor="#12B76A"
      backgroundColor="#D1FADF"
      color="#027A48"
    />
  </Grid>
);

const CheckStatusRow = ({ check }: { check: ICheckResult }) => (
  <Flex alignItems="center" gap={2}>
    <IntegrityStatusIcon severity={check.status} />
    <Text textStyle="textBody">
      {check.result?.title ?? CheckTypeMap[check.checkType]}
    </Text>
    <Text textStyle="textContext">{statusLabels[check.status]}</Text>
  </Flex>
);

/**
 * The author's view of the integrity checks on the submission they are about
 * to submit: run them, watch them finish, and read what they found.
 *
 * The form above it refuses to submit while a configured check has not run or
 * has reported a risk, so this panel is also where a blocked author is sent.
 */
const AuthorIntegrityChecks: React.FC<IProps> = ({
  integrity,
  submissionId,
  submissionContextId,
  showNotRunError,
  containerRef
}) => {
  const toast = useToast();

  // The journal runs no author checks: there is nothing to offer or to gate on.
  if (!integrity.configuredChecks.length) return null;

  const handleRun = async () => {
    const response = await integrity.startRun();

    if (!response.isSuccess) {
      toast({
        variant: 'error',
        title: 'Integrity checks could not be run',
        description: runErrorMessage(response.message),
        isClosable: true
      });
    }
  };

  const body = () => {
    if (integrity.gate === 'running') {
      return (
        <OutlineCard padding={4}>
          <CardHeader
            action={
              integrity.isFetching ? (
                <Text textStyle="textContext">Checking for results…</Text>
              ) : null
            }
          />
          <Flex flexDirection="column" gap={3} marginTop={4}>
            {integrity.checks.map(check => (
              <CheckStatusRow key={check.checkType} check={check} />
            ))}
          </Flex>
          <Flex justifyContent="flex-end" marginTop={4}>
            <Button
              variant="secondary"
              size="context"
              onClick={() => integrity.refresh()}
            >
              Refresh
            </Button>
          </Flex>
        </OutlineCard>
      );
    }

    if (integrity.gate === 'not-run') {
      return (
        <OutlineCard padding={4}>
          <Flex gap={3} alignItems="flex-start">
            <IconBadge />
            <Box flex="1">
              <Text textStyle="headingItem">Integrity Checks</Text>
              <Text textStyle="textContext">{headerCopy}</Text>
              {showNotRunError && (
                <Flex alignItems="center" gap={2} marginTop={2}>
                  <FormFeedback variant="error">
                    It is essential to conduct Integrity checks.
                  </FormFeedback>
                </Flex>
              )}
            </Box>
            <ButtonText
              size="small"
              disabled={integrity.isStarting}
              loading={{
                isLoading: integrity.isStarting,
                loadingText: 'Starting'
              }}
              onClick={handleRun}
            >
              Run Checks
            </ButtonText>
          </Flex>
        </OutlineCard>
      );
    }

    return (
      <Flex flexDirection="column" gap={3}>
        {integrity.tally.risk > 0 && (
          <Banner
            variant="attention"
            customIcon={<FontAwesomeIcon icon={faTriangleExclamation} />}
            heading={`Action Required — ${integrity.tally.risk} Risks & ${integrity.tally.warning} Warning`}
            content="Submission cannot proceed if any risks are identified. Please correct those issues before you continue."
          />
        )}
        <Accordion
          data-testId="revisions-history-overview"
          chakraAccordionProps={{
            allowMultiple: true,
            defaultIndex: [0],
            style: {
              display: 'flex',
              flexDirection: 'column'
            }
          }}
          items={[
            {
              title: `Integrity Checks`,
              content: (
                <Box>
                  <Tiles tally={integrity.tally} />
                  <Box marginTop={4}>
                    <CheckList
                      checks={integrity.checks}
                      roundId={integrity.checks[0]?.roundId ?? '0'}
                      submissionId={submissionId}
                      // Only the editor-facing detailed report needs a
                      // workflow state, and this panel never offers one.
                      submissionContextId={submissionContextId}
                      currentState=""
                    />
                  </Box>
                </Box>
              )
            }
          ]}
        />
      </Flex>
    );
  };

  return (
    <Box ref={containerRef} id="integrity-checks" marginTop={4}>
      {body()}
      {/* In flight is exactly the window before the panel has rows to poll. */}
      <RunningChecksModal isOpen={integrity.isStarting} />
    </Box>
  );
};

export default AuthorIntegrityChecks;
